import React, { useState, ReactNode } from 'react';
import styled from '@emotion/styled';
import { MediaFile } from './useMediaLib';
import { IconButton, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Add, Close, InsertDriveFile } from '@mui/icons-material';
import { MediaLibPickerDialog } from './MediaLibPickerDialog';

interface Props {
  selectedUrls: string[] | null;
  selectFiles: (mf: MediaFile[] | null, urls: string[] | null) => void;
  label?: ReactNode;
  placeholder?: ReactNode;
  disabled?: boolean;
  noUpload?: boolean;
}

const extractFilename = (url: string) => url.split('/').pop()?.split(/[?#]/)[0] || 'Unknown File';
const checkIsImage = (filename: string) => ["png", "jpg", "jpeg", "webp", "svg"].includes(filename.toLowerCase().split(".").pop() || "no");

export const MediaLibListMultiPicker = (props: Props) => {
  const { selectedUrls, selectFiles, label, disabled } = props;
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  const handleRemove = (urlToRemove: string) => {
    if (!selectedUrls) return;
    const newUrls = selectedUrls.filter(u => u !== urlToRemove);
    selectFiles(null, newUrls);
  };

  const isEmpty = !selectedUrls || selectedUrls.length === 0;

  return (
    <>
      <ListPickerWrapper className="medialib-list-picker">
        <div className="header">
           {label &&
            <Typography variant="caption" color="textSecondary">
              {label} ({selectedUrls?.length || 0})
            </Typography>}
           <IconButton 
              size="small" 
              onClick={() => !disabled && setIsDialogOpen(true)}
              disabled={disabled}
              color="primary"
            >
              <Add />
           </IconButton>
        </div>

        {isEmpty ? (
          <div className="placeholder" onClick={() => !disabled && setIsDialogOpen(true)}>
            {props.placeholder || (<Typography color="textSecondary"><FormattedMessage id="medialib.select" /></Typography>)}
          </div>
        ) : (
          <ScrollableList>
            {selectedUrls!.map((url) => {
              const filename = extractFilename(url);
              const isImage = checkIsImage(filename);
              
              return (
                <FileRow key={url}>
                  <div className="preview">
                    {isImage ? (
                      <img src={url} alt={filename} />
                    ) : (
                      <InsertDriveFile fontSize="small" color="action" />
                    )}
                  </div>
                  <Typography variant="body2" className="filename" title={filename}>
                    {decodeURIComponent(filename)}
                  </Typography>
                  <IconButton 
                    size="small" 
                    className="remove-btn"
                    onClick={(e) => { e.stopPropagation(); handleRemove(url); }}
                    disabled={disabled}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </FileRow>
              );
            })}
          </ScrollableList>
        )}
      </ListPickerWrapper>

      <MediaLibPickerDialog
        isOpen={isDialogOpen}
        close={() => setIsDialogOpen(false)}
        selectedUrls={selectedUrls || []}
        // Multi-select logic for the dialog:
        selectFile={(f, url) => selectFiles(null, [...(selectedUrls || []), url])}
        unselectFile={(f, url) => selectFiles(null, (selectedUrls || []).filter(u => u !== url))}
        noUpload={props.noUpload}
      />
    </>
  );
}

// --- Styles ---

const ListPickerWrapper = styled.div`
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 100px; /* Ensure some height even when empty */
  overflow: hidden;
  width: 100%;

  & .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  & .placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-grow: 1;
    cursor: pointer;
    background: ${props => props.theme.palette.grey[50]};
    border-radius: 4px;
    border: 1px dashed ${props => props.theme.palette.divider};
    min-height: 3rem;
    transition: background 0.2s;
    
    &:hover {
        background: ${props => props.theme.palette.grey[100]};
    }
  }
`;

const ScrollableList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: 300px;
  overflow-y: auto;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem;
  border-radius: 4px;
  background: ${props => props.theme.palette.grey[50]};
  border: 1px solid transparent;

  &:hover {
    background: ${props => props.theme.palette.grey[100]};
    border-color: ${props => props.theme.palette.divider};
  }

  & .preview {
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
    background: #fff;
    border-radius: 2px;
    border: 1px solid ${props => props.theme.palette.divider};

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  & .filename {
    flex-grow: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & .remove-btn {
    opacity: 0.6;
    &:hover {
      opacity: 1;
      color: ${props => props.theme.palette.error.main};
    }
  }
`;
